/**
 * Robustness Pipeline Mock
 *
 * Shows how a strategy's reported performance degrades as
 * progressively more realistic checks are applied:
 * raw backtest → after costs → out-of-sample → multi-asset.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faTriangleExclamation,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';

interface Stage {
  label: string;
  returnPct: string;
  sharpe: string;
  maxDD: string;
  verdict: 'pass' | 'warn' | 'fail';
  note: string;
}

const stages: Stage[] = [
  {
    label: 'Raw Backtest',
    returnPct: '+86.4%',
    sharpe: '1.92',
    maxDD: '-11.2%',
    verdict: 'pass',
    note: 'Frictionless simulation — no costs applied',
  },
  {
    label: 'After Fees & Slippage',
    returnPct: '+51.7%',
    sharpe: '1.24',
    maxDD: '-14.8%',
    verdict: 'warn',
    note: '0.1% fee + 0.05% slippage per trade',
  },
  {
    label: 'Out-of-Sample',
    returnPct: '+18.3%',
    sharpe: '0.61',
    maxDD: '-22.5%',
    verdict: 'warn',
    note: 'Tested on unseen data period',
  },
  {
    label: 'Multi-Asset Check',
    returnPct: '+4.1%',
    sharpe: '0.29',
    maxDD: '-31.4%',
    verdict: 'fail',
    note: 'Average across 5 symbols',
  },
];

const verdictConfig = {
  pass: { icon: faCheck, color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
  warn: { icon: faTriangleExclamation, color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  fail: { icon: faXmark, color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
};

function MetricCell({ label, value, dim }: { label: string; value: string; dim?: boolean }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: '0.5625rem', color: '#6b7280', marginBottom: '0.1rem',
        textTransform: 'uppercase', letterSpacing: '0.05em',
      }}>{label}</div>
      <div style={{
        fontSize: '0.8125rem', fontWeight: 600, color: dim ? '#6b7280' : '#d1d5db',
        fontFamily: 'ui-monospace, monospace',
      }}>{value}</div>
    </div>
  );
}

export default function RobustnessPipelineMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Robustness Pipeline
      </div>
      <div className="platform-mock-body" style={{ padding: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {stages.map((s, i) => {
            const vc = verdictConfig[s.verdict];
            return (
              <div key={i} style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto auto auto',
                gap: '0.75rem', alignItems: 'center',
                padding: '0.625rem 0.75rem',
                background: '#181825',
                border: `1px solid rgba(255,255,255,0.06)`,
                borderRadius: 6,
              }}>
                {/* Stage label + note */}
                <div>
                  <div style={{
                    fontSize: '0.75rem', fontWeight: 600, color: '#e5e7eb',
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                  }}>
                    <span style={{
                      width: 18, height: 18, borderRadius: '50%',
                      background: vc.bg, display: 'inline-flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <FontAwesomeIcon icon={vc.icon} style={{ fontSize: '0.5rem', color: vc.color }} />
                    </span>
                    {s.label}
                  </div>
                  <div style={{ fontSize: '0.5625rem', color: '#6b7280', marginTop: '0.15rem', paddingLeft: '1.65rem' }}>
                    {s.note}
                  </div>
                </div>
                <MetricCell label="Return" value={s.returnPct} />
                <MetricCell label="Sharpe" value={s.sharpe} dim={parseFloat(s.sharpe) < 0.5} />
                <MetricCell label="Max DD" value={s.maxDD} />
                {/* Arrow to next */}
                {i < stages.length - 1 ? (
                  <div style={{ color: '#4b5563', fontSize: '0.75rem', textAlign: 'center' }}>&darr;</div>
                ) : (
                  <div style={{ width: 12 }} />
                )}
              </div>
            );
          })}
        </div>

        <div style={{
          marginTop: '0.75rem', padding: '0.5rem 0.75rem',
          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)',
          borderRadius: 6, fontSize: '0.6875rem', color: '#9ca3af',
        }}>
          <span style={{ color: '#ef4444', fontWeight: 600 }}>Result: </span>
          What looked like +86% in a raw backtest becomes +4% when tested properly.
          Most of the apparent edge was an artefact of unrealistic assumptions.
        </div>
      </div>
      <div className="platform-mock-caption">
        Each validation stage strips away false confidence. What survives is what matters.
      </div>
    </div>
  );
}
